'use client';
import Link from 'next/link';
import {
  SectLabel,
  SectLede,
  V4CtaButton,
  V4GhostButton,
} from '@/components/landing/v4-shared';
import { MagneticCta } from '@/components/marketing/MagneticCta';
import {
  BRAND,
  INTEGRATIONS_PARTNER_COPY,
} from '@/lib/marketing/messaging-bank';
import { IntegrationLogoMark } from './IntegrationLogoMark';

interface PartnerHeroProps {
  slug: string;
  name: string;
  categoryLabel: string;
  tagline: string;
  partnerUrl: string;
}

/**
 * V4 cyan-pillar slug-page hero for /integrations/[partner].
 *
 * Renders the BizRnR + partner pair with a glowing cyan tether between
 * them; the partner mark carries `viewTransitionName` matching the
 * index card so navigation morphs the icon across the cross-document
 * transition. Magnetic-feel cursor on the primary CTA via the shared
 * MagneticCta primitive (motion-gated by `prefers-reduced-motion`).
 */
export function PartnerHero({
  slug,
  name,
  categoryLabel,
  tagline,
  partnerUrl,
}: PartnerHeroProps) {
  // Templated copy
  const eyebrow = INTEGRATIONS_PARTNER_COPY.eyebrow!.replace(
    '{INTEGRATION_CATEGORIES[integration.category].label}',
    categoryLabel,
  );

  return (
    <section className="integrations-hero" aria-labelledby="partner-hero-heading">
      <div style={{ padding: '0 24px' }}>
        <nav aria-label="Breadcrumb" className="integrations-crumbs">
          <Link href="/">Home</Link>
          <span className="integrations-crumbs__sep" aria-hidden="true">/</span>
          <Link href="/integrations">Integrations</Link>
          <span className="integrations-crumbs__sep" aria-hidden="true">/</span>
          <span className="integrations-crumbs__here">{name}</span>
        </nav>

        <div className="integrations-pair" aria-hidden="true">
          <IntegrationLogoMark
            name="BizRnR"
            variant="bizrnr"
            size={96}
            style={{ borderRadius: 22 }}
          />
          <span className="integrations-pair__beam" />
          <IntegrationLogoMark
            name={name}
            size={96}
            style={{
              borderRadius: 22,
              viewTransitionName: `integration-mark-${slug}`,
            }}
          />
        </div>

        <SectLabel color="var(--cyan-glow)">
          {eyebrow}
        </SectLabel>

        <h1 id="partner-hero-heading" className="integrations-hero__h1">
          BizRnR <em>+ {name}</em>
        </h1>
        <SectLede className="speakable-summary">{tagline}</SectLede>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <MagneticCta>
            <V4CtaButton
              href={`${INTEGRATIONS_PARTNER_COPY.ctaPrimary.href}?integration=${encodeURIComponent(slug)}`}
              size="lg"
            >
              {INTEGRATIONS_PARTNER_COPY.ctaPrimary.label}
            </V4CtaButton>
          </MagneticCta>
          <V4GhostButton href={partnerUrl} variant="cyan" size="lg">
            {`Visit ${name} →`}
          </V4GhostButton>
        </div>
        <p
          style={{
            marginTop: 18,
            fontFamily: 'var(--font-geist-mono)',
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--dim)',
          }}
        >
          {BRAND.primaryCtaShort}
        </p>
      </div>
    </section>
  );
}
